import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { omit } from "lodash";

import DocumentsToSubmit from "Components/DocumentsToSubmit";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";

import {
  convertDocumentKeys,
  filterEmptyStrings,
  generateFormData,
} from "utils";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { useLazyApplicationDetailQuery } from "apis/ApplicationRequestDetail";
import { usePostUploadPendingDocumentsMutation } from "apis/DocumentType";
import { useLazyGetOtherApplicationDetailQuery } from "apis/otherApplicationDetails";

const PartialDocumentUploadModal = ({ isPowerApplication = false, t }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const applicationRequestId = searchParams.get("applicationRequestId");

  const [
    getApplicationData,
    { isLoading, data: { data: applicationData } = {} },
  ] = useLazyApplicationDetailQuery();

  const [
    getOtherApplicationData,
    {
      isLoading: isOtherApplicationDetailsLoading,
      data: { data: otherApplicationData } = {},
    },
  ] = useLazyGetOtherApplicationDetailQuery();

  const [uploadDocuments, { isLoading: isDocumentUploading }] =
    usePostUploadPendingDocumentsMutation();

  const initialValues = useMemo(
    () => ({
      applicant: {
        applicantTypeId:
          (applicationData ?? otherApplicationData)?.applicant?.applicantType
            ?.id ?? "",
        occupancyTypeId:
          (applicationData ?? otherApplicationData)?.applicant?.occupancyType
            ?.id ?? "",
      },
      applicationTypeId:
        (applicationData ?? otherApplicationData)?.applicationType?.id ?? "",
      utilities:
        (applicationData ?? otherApplicationData)?.utilities?.map((value) => ({
          value,
        })) ?? [],
      utilityAddress: {
        premisesAreaId:
          (applicationData ?? otherApplicationData)?.utilityAddress?.premiseArea
            ?.id ?? "",
        isBPL:
          (applicationData ?? otherApplicationData)?.utilityAddress?.isBPL ??
          false,
      },

      connectionLoad: {
        tention: applicationData?.connectionLoad?.powerRequirement
          ? applicationData.connectionLoad.powerRequirement < 100
            ? "LT"
            : "HT"
          : "",
        increaseOfLoad: applicationData?.connectionLoad?.increaseOfLoad ?? "",
        decreaseOfLoad: applicationData?.connectionLoad?.decreaseOfLoad ?? "",
        isIGSTExempted: applicationData?.isIGSTExempted ?? false,
      },

      parentId:
        (applicationData ?? otherApplicationData)?.documents.find(
          ({ parentId }) => !!parentId
        )?.parentId ?? "",
      documents:
        (applicationData ?? otherApplicationData)?.documents?.filter(
          ({ isApproved, id }) => !isApproved && id
        ) ?? [],
      proofOfOwnershipDescription: "",
      id: (applicationData ?? otherApplicationData)?.id ?? "",
      extraValues: {
        requiredDocumentsCount: 0,
      },
    }),
    [applicationData, otherApplicationData]
  );

  const handleClose = () => {
    searchParams.delete("applicationRequestId");
    setSearchParams(searchParams);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      documents: getDocumentsValidationSchema(),
    }),
    onSubmit: async ({ documents, proofOfOwnershipDescription }) => {
      try {
        const formData = new FormData();
        const updatedValues = convertDocumentKeys({
          documents,
          proofOfOwnershipDescription,
        });
        const payload = filterEmptyStrings(
          omit(updatedValues, ["documents", "proofOfOwnershipDescription"])
        );
        generateFormData(formData, payload);
        await uploadDocuments(
          { applicationId: applicationRequestId },
          formData
        );
        handleClose();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    values,
    values: { documents },
  } = formik;

  const getApplicationDataByApplicationId = async (applicationId) => {
    try {
      if (isPowerApplication) {
        await getApplicationData(applicationId);
      } else {
        await getOtherApplicationData({ applicationId: applicationId });
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (applicationRequestId) {
      getApplicationDataByApplicationId(applicationRequestId);
    }
  }, [applicationRequestId]);

  if (
    isLoading ||
    isOtherApplicationDetailsLoading ||
    !values.applicationTypeId
  ) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Upload Documents"
      maxWidth="md"
    >
      <>
        <div className="my-3">
          <DocumentsToSubmit formik={formik} />
        </div>
        <Button
          onClick={handleSubmit}
          color="primary"
          className="d-block ms-auto"
          variant="contained"
          disabled={isDocumentUploading || !documents.length}
        >
          {t("Upload")} {isDocumentUploading && <CircularProgress size={20} />}
        </Button>
      </>
    </Modal>
  );
};

PartialDocumentUploadModal.propTypes = {
  isPowerApplication: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(PartialDocumentUploadModal);
